export enum EventType {
  /**
   * Event type when viewport's appearance changed.
   */
  APPEARANCE_CHANGED             = 'APPEARANCE_CHANGED',

  /**
   * Event type when viewport's breakpoint changed.
   */
  BREAKPOINT_CHANGED             = 'BREAKPOINT_CHANGED',

  /**
   * Event type for requesting to show a notification that a value was copied to the clipboard. Arguments: [ string ].
   */
  COPY_TO_CLIPBOARD_NOTIFICATION = 'COPY_TO_CLIPBOARD_NOTIFICATION',

  /**
   * Event type for requesting to show a POI's details. Arguments: [ Poi ]
   */
  SELECT_POI                     = 'SELECT_POI',

  /**
   * Event type indicating a POI's details view was dismissed.
   */
  ON_POI_UNSELECTED              = 'ON_POI_UNSELECTED',

  /**
   * Event type indicating a POI's data has changed. Arguments: [ Poi ]
   */
  ON_POI_UPDATED                 = 'ON_POI_UPDATED',

  /**
   * Event type indicating zero or more POIs are currently in a hover state. Arguments: [ Poi[] ]
   */
  ON_POIS_HOVERING               = 'ON_POIS_HOVERING',

  /**
   * Event type for requesting a location to be centered in the MapView. Arguments: [ LatLng ]
   */
  CENTER_IN_MAP                  = 'CENTER_IN_MAP',

  /**
   * Event type for requesting the MapView to zoom out to show the entire trip.
   */
  SHOW_TRIP_IN_MAP               = 'SHOW_TRIP_IN_MAP',

  /**
   * Event type for requesting a POI to be highlighted in the itinerary list. Arguments: [ Poi ]
   */
  HIGHLIGHT_POI                  = 'HIGHLIGHT_POI',

  /**
   * Event type for requesting a new popup menu. Arguments: [ PopupMenuViewArgs ]
   */
  POPUPMENU_OPEN                 = 'POPUPMENU_OPEN',

  /**
   * Event type for requesting to close the popup menu.
   */
  POPUPMENU_CLOSE                = 'POPUPMENU_CLOSE',
}

/**
 * A simple event bus implementation.
 */
export const Events = {

  /**
   * Subscribes to an event.
   *
   * @param {string} name Name of the event.
   * @param {(detail: any) => void} callback Callback to be invoked when the event is published.
   */
  subscribe: (name: string, callback: (detail: any) => void): EventSubscription => {
    const handler = (e: any) => {
      callback(e.detail);
    };

    document.addEventListener(name, handler);

    return {
      unsubscribe: () => {
        document.removeEventListener(name, handler);
      }
    };
  },

  /**
   * Publishes an event.
   *
   * @param {string} name Name of the event.
   * @param args Arguments to be passed to the event listeners.
   */
  publish: (name: string, args?: any): void => {
    document.dispatchEvent(new CustomEvent(name, { detail: args }));
  },

  /**
   * Collects multiple event subscriptions and allows to unsubscribe them all at once.
   */
  collect: (): SubscriptionCollector => {
    const subs: EventSubscription[] = [];

    return {
      subscribe: (name: string, callback: (detail: any) => void) => {
        subs.push(Events.subscribe(name, callback));
      },
      cancel:    () => {
        subs.forEach(sub => sub.unsubscribe());
      }
    };
  }

};

/**
 * Interface for a collection of event subscriptions.
 */
export interface SubscriptionCollector {
  subscribe: (name: string, callback: any) => void;
  cancel: () => void;
}

/**
 * Interface for an event subscription.
 */
export interface EventSubscription {
  unsubscribe: () => void;
}
